import { MODULES, type ModuleId, type ModuleMeta } from '@tahi/core';
import { useState } from 'react';
import { setModuleEnabled } from '../../data/commands/modules';
import { useCommandEnv, useSyncStatus } from '../../data/hooks';
import { listModuleStates } from '../../data/repos/moduleStates';
import { getState, setState } from '../../data/syncState';
import { useQuery } from '../../data/useQuery';
import { getWebModule } from '../../modules/registry';
import { Button } from '../../ui/Button';
import { Switch } from '../../ui/Switch';
import { MODULE_DESCRIPTIONS } from '../modules/moduleCopy';

const DONE_KEY = 'onboarding_done';

/** A fresh account: no module rows, the picker not finished or skipped, and the first sync has had its say. */
export function useNeedsOnboarding(): boolean {
  const status = useSyncStatus();
  const { data } = useQuery(['module_states', 'sync_state'], async (db) => ({
    rows: (await listModuleStates(db)).length,
    done: Boolean(await getState(db, DONE_KEY)),
  }));
  const syncedOnce =
    status.lastSyncedAt !== null || status.state === 'offline' || status.state === 'error';
  return Boolean(data && data.rows === 0 && !data.done && syncedOnce);
}

export function Onboarding() {
  const env = useCommandEnv();
  const choices = (Object.values(MODULES) as ModuleMeta[]).filter(
    (m) => m.id !== 'today' && !m.alwaysOn,
  );
  const [picked, setPicked] = useState<Set<ModuleId>>(
    () => new Set(getWebModule('spending') ? ['spending'] : []),
  );
  const [busy, setBusy] = useState(false);

  async function finish(turnOn: ModuleId[]) {
    setBusy(true);
    try {
      for (const id of turnOn) await setModuleEnabled(env, id, true);
      await setState(env.db, DONE_KEY, new Date().toISOString());
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-labelledby="onboarding-title"
      className="flex flex-col gap-4 rounded-[24px] border border-divider bg-surface-2 p-4"
    >
      <div>
        <h2 id="onboarding-title" className="text-xl font-bold">
          Pick your modules
        </h2>
        <p className="text-sm text-muted">
          Turn on what you want to track. You can change this any time in Modules.
        </p>
      </div>
      <ul className="flex flex-col gap-2">
        {choices.map((m) => {
          const available = getWebModule(m.id) !== undefined;
          return (
            <li key={m.id} className="flex items-center gap-3">
              <div className="flex-1">
                <p className="font-medium">{m.name}</p>
                <p className="text-sm text-muted">
                  {available ? MODULE_DESCRIPTIONS[m.id] : 'Coming soon'}
                </p>
              </div>
              <Switch
                label={m.name}
                checked={picked.has(m.id)}
                disabled={!available || busy}
                onCheckedChange={(on) =>
                  setPicked((prev) => {
                    const next = new Set(prev);
                    if (on) next.add(m.id);
                    else next.delete(m.id);
                    return next;
                  })
                }
              />
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2">
        <Button variant="primary" disabled={busy} onClick={() => void finish([...picked])}>
          Start
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => void finish([])}>
          Skip for now
        </Button>
      </div>
    </section>
  );
}
